import { FieldArray, Form, Formik } from "formik"
import { useMemo } from "react"
import { Accordion, Col, FormCheck } from "react-bootstrap"
import { useLocation } from "react-router-dom"

import TextInput from "@/components/Inputs/TextInput"
import { MODALS } from "@/constants/modals"
import type {
  CourseContent,
  CourseContentItem,
  Lesson,
  Topic,
} from "@/types/courseTypes"
import { useStore } from "@/zustand/store"
import { useTranslation } from "react-i18next"

export default function LectureContentModal() {
  const lecture = useStore(
    (store) => store.modalsStates[MODALS.lecture.content]
  )

  const { content } = lecture || {}

  const initialValues = useMemo(() => {
    if (!content) {
      return [] as CourseContent
    }

    if (!lecture?.content) {
      return content
    }

    const round_content = lecture?.content?.map((chapter) => ({
      id: chapter.id,
      order: chapter.order,
      name: chapter.name || "",
      is_checked: chapter.is_checked ?? "0",
      lessons: Array.isArray(chapter.lessons)
        ? chapter.lessons.map((lesson) => ({
            id: lesson.id,
            title: lesson.title || "",
            order: lesson.order,
            is_checked: lesson.is_checked ?? "0",
            topics: Array.isArray(lesson.topics)
              ? lesson.topics.map((topic) => ({
                  id: topic.id,
                  order: topic.order,
                  title: topic.title || "",
                  is_checked: topic.is_checked ?? "0",
                }))
              : [],
          }))
        : [],
    }))

    return round_content?.filter((chapter) => {
      chapter.lessons = chapter?.lessons?.filter((lesson) => {
        lesson.topics = lesson?.topics?.filter((topic) => {
          return topic?.is_checked !== "1"
        })
        return lesson?.topics?.length || lesson?.is_checked === "1"
      })
      return chapter?.lessons?.length || chapter?.is_checked === "1"
    }) as CourseContent
  }, [lecture])

  const { t } = useTranslation()

  return (
    <Formik
      initialValues={{ round_content: initialValues }}
      onSubmit={() => {}}
    >
      {({ values }) => {
        return (
          <Form>
            <Col md={12} className="mb-3">
              <p className="fw-bold mb-2">
                {t("enrolledCoursesPage.lecturesDetails.lectureContent")}
              </p>
              <FieldArray
                name="round_content"
                render={() => (
                  <Accordion defaultActiveKey="0">
                    {values.round_content?.map((chapter, chapterIndex) => (
                      <ChapterItem
                        key={chapter.id}
                        chapterIndex={chapterIndex}
                        chapter={chapter}
                      />
                    ))}
                  </Accordion>
                )}
              />
            </Col>
          </Form>
        )
      }}
    </Formik>
  )
}

function TopicItem({
  topic,
  chapterIndex,
  lessonIndex,
  topicIndex,
}: {
  topic: Topic
  chapterIndex: number
  lessonIndex: number
  topicIndex: number
}) {
  // const { values, setFieldValue } = useFormikContext<{
  //   round_content: CourseContent
  // }>()

  // useEffect(
  //   () =>
  //     updateCheckedLessonFromTopic(
  //       values,
  //       setFieldValue,
  //       chapterIndex,
  //       lessonIndex
  //     ),
  //   [topic.is_checked]
  // )

  const isCourse = useLocation().pathname.includes("course")

  return (
    <div key={topicIndex} className="d-flex mb-2 ps-5 gap-2">
      <TextInput
        readOnly
        id={`round_content.${chapterIndex}.lessons.${lessonIndex}.topics.${topicIndex}.title`}
        label={``}
        placeholder={`Topic ${topicIndex + 1}`}
        name={`round_content.${chapterIndex}.lessons.${lessonIndex}.topics.${topicIndex}.title`}
        as="field"
      />
      {!isCourse && (
        <FormCheck
          readOnly
          type="checkbox"
          className="mt-3"
          checked={topic.is_checked === "1"}
          // onChange={(e) => {
          //   setFieldValue(
          //     `round_content.${chapterIndex}.lessons.${lessonIndex}.topics.${topicIndex}.is_checked`,
          //     e.target.checked ? "1" : "0"
          //   )

          //   updateCheckedLessonFromTopic(
          //     values,
          //     setFieldValue,
          //     chapterIndex,
          //     lessonIndex
          //   )
          // }}
        />
      )}
    </div>
  )
}

function LessonItem({
  lesson,
  lessonIndex,
  chapterIndex,
}: {
  lesson: Lesson
  lessonIndex: number
  chapterIndex: number
}) {
  // const { values, setFieldValue } = useFormikContext<{
  //   round_content: CourseContent
  // }>()

  // useEffect(() => {
  //   updateCheckedChapterFromLessons(values, setFieldValue, chapterIndex)
  //   updateCheckedTopicsFromLesson(
  //     values,
  //     setFieldValue,
  //     chapterIndex,
  //     lessonIndex
  //   )
  // }, [lesson.is_checked])

  const isCourse = useLocation().pathname.includes("course")

  return (
    <div key={lessonIndex}>
      <div className="d-flex align-items-center mb-2 ps-4 gap-2">
        <TextInput
          readOnly
          id={`round_content.${chapterIndex}.lessons.${lessonIndex}.title`}
          label={`Lesson ${lessonIndex + 1}`}
          placeholder={`Lesson ${lessonIndex + 1}`}
          name={`round_content.${chapterIndex}.lessons.${lessonIndex}.title`}
          as="field"
        />
        {!isCourse && (
          <FormCheck
            readOnly
            type="checkbox"
            className="mt-3"
            checked={lesson.is_checked === "1"}
            // onChange={(e) => {
            //   setFieldValue(
            //     `round_content.${chapterIndex}.lessons.${lessonIndex}.is_checked`,
            //     e.target.checked ? "1" : "0"
            //   )
            // }}
          />
        )}
      </div>

      {lesson?.topics?.map((topic, topicIndex) => (
        <TopicItem
          key={topic.id}
          topic={topic}
          chapterIndex={chapterIndex}
          lessonIndex={lessonIndex}
          topicIndex={topicIndex}
        />
      ))}
    </div>
  )
}

function ChapterItem({
  chapterIndex,
  chapter,
}: {
  chapterIndex: number
  chapter: CourseContentItem
}) {
  // const { values, setFieldValue } = useFormikContext<{
  //   round_content: CourseContent
  // }>()

  // useEffect(
  //   () => updateCheckedLessonsFromChapter(values, setFieldValue, chapterIndex),
  //   [chapter.is_checked]
  // )

  const isCourse = useLocation().pathname.includes("course")

  return (
    <Accordion.Item eventKey={String(chapterIndex)} key={chapterIndex}>
      <Accordion.Header>
        {chapter.name || `Chapter ${chapterIndex + 1}`}
      </Accordion.Header>
      <Accordion.Body>
        <div className="d-flex align-items-center gap-2  mb-2">
          <TextInput
            readOnly
            id={`round_content.${chapterIndex}.name`}
            label={`Chapter ${chapterIndex + 1}`}
            placeholder="Chapter title"
            name={`round_content.${chapterIndex}.name`}
            as="field"
          />
          {!isCourse && (
            <FormCheck
              readOnly
              type="checkbox"
              className="mt-3"
              checked={chapter.is_checked === "1"}
              // onChange={(e) => {
              //   setFieldValue(
              //     `round_content.${chapterIndex}.is_checked`,
              //     e.target.checked ? "1" : "0"
              //   )
              // }}
            />
          )}
        </div>

        <FieldArray
          name={`round_content.${chapterIndex}.lessons`}
          render={() => (
            <>
              {chapter?.lessons?.map((lesson, lessonIndex) => (
                <LessonItem
                  key={lessonIndex}
                  lesson={lesson}
                  lessonIndex={lessonIndex}
                  chapterIndex={chapterIndex}
                />
              ))}
            </>
          )}
        />
      </Accordion.Body>
    </Accordion.Item>
  )
}

// function updateCheckedLessonFromTopic(
//   values: { round_content: CourseContent },
//   setValue: (field: string, value: unknown) => void,
//   chapterIndex: number,
//   lessonIndex: number
// ) {
//   if (
//     values?.round_content?.[chapterIndex]?.lessons?.[
//       lessonIndex
//     ]?.topics?.every((topic) => topic.is_checked === "1")
//   ) {
//     setValue(
//       `round_content.${chapterIndex}.lessons.${lessonIndex}.is_checked`,
//       "1"
//     )
//   } else {
//     setValue(
//       `round_content.${chapterIndex}.lessons.${lessonIndex}.is_checked`,
//       "0"
//     )
//   }

//   updateCheckedChapterFromLessons(values, setValue, chapterIndex)
// }

// function updateCheckedChapterFromLessons(
//   values: { round_content: CourseContent },
//   setValue: (field: string, value: unknown) => void,
//   chapterIndex: number
// ) {
//   if (
//     values?.round_content?.[chapterIndex]?.lessons?.every(
//       (lesson) => lesson.is_checked === "1"
//     )
//   ) {
//     setValue(`round_content.${chapterIndex}.is_checked`, "1")
//   } else {
//     setValue(`round_content.${chapterIndex}.is_checked`, "0")
//   }
// }

// function updateCheckedTopicsFromLesson(
//   values: { round_content: CourseContent },
//   setValue: (field: string, value: unknown) => void,
//   chapterIndex: number,
//   lessonIndex: number
// ) {
//   const lessonValue =
//     values?.round_content?.[chapterIndex]?.lessons?.[lessonIndex]
//       ?.is_checked === "1"

//   values?.round_content?.[chapterIndex]?.lessons?.[
//     lessonIndex
//   ]?.topics?.forEach((_, topicIndex) => {
//     setValue(
//       `round_content.${chapterIndex}.lessons.${lessonIndex}.topics.${topicIndex}.is_checked`,
//       lessonValue ? "1" : "0"
//     )
//   })
// }

// function updateCheckedLessonsFromChapter(
//   values: { round_content: CourseContent },
//   setValue: (field: string, value: unknown) => void,
//   chapterIndex: number
// ) {
//   if (values.round_content[chapterIndex].is_checked === "1") {
//     values?.round_content?.[chapterIndex]?.lessons?.forEach(
//       (_, lessonIndex) => {
//         setValue(
//           `round_content.${chapterIndex}.lessons.${lessonIndex}.is_checked`,
//           "1"
//         )
//       }
//     )
//   } else {
//     values?.round_content?.[chapterIndex]?.lessons?.forEach(
//       (_, lessonIndex) => {
//         setValue(
//           `round_content.${chapterIndex}.lessons.${lessonIndex}.is_checked`,
//           "0"
//         )
//       }
//     )
//   }
// }
